import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cs from 'classnames';
import { Box, Grid } from '@material-ui/core';
import Validation from '../../../../components/Validation/Validation';
import { updateCascoForm, submitForm } from '../../../../redux/modules/cascoClaim';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import { CONCLUSION_STE_FIELDS } from '../../options';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import MainRadio from '../../../../components/MUI/MainRadio';
import MainCheckbox from '../../../../components/MUI/MainCheckbox/MainCheckbox';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import InfoText from '../../../../components/MUI/IntoText';
import conclusionSchema from '../../../../validations/claimValidationSchema/casco/conclusionSchema';
import { NON_DIGITS_PATTERN } from '../../../../constants/patterns';
import FormSection from '../../../../components/FormComponents/FormSection';

import UserInfoIcon from '../../../../img/formSectionIcons/userInfo.svg';
import styles from '../../../CompensationContainer/Steps/steps.module.scss';

const RECEIVED_COMPENSATION_OPTIONS = [
  {
    value: 'notReceived',
    label: 'claim.conclusion.receivedCompensation.notReceived',
  },
  {
    value: 'received',
    label: 'claim.conclusion.receivedCompensation.received',
  },
];

const CascoConclusionStep = ({
  goNext,
  step,
  goPrevious,
  params: { setStepNavigationBox, scrollTo },
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const getValuesRef = useRef();
  const isDeviceLgSize = useDeviceResize('lg');

  const cascoClaim = useSelector((store) => store.cascoClaim, shallowEqual);
  const initialData = cascoClaim.conclusion;

  const [receivedCompensation, setReceivedCompensation] = useState(
    (initialData && initialData.receivedCompensation) || RECEIVED_COMPENSATION_OPTIONS[0].value,
  );

  const [receivedCompensationSum, setReceivedCompensationSum] = useState(
    (initialData && initialData.receivedCompensationSum) || null,
  );

  const [hasAgreed, setHasAgreed] = useState((initialData && initialData.hasAgreed) || false);

  useEffect(
    // save values in store on unmount
    () => () => {
      if (getValuesRef.current) {
        const data = getValuesRef.current();
        dispatch(updateCascoForm({ name: 'conclusion', data }));
      }
    },
    [dispatch],
  );

  const {
    handleSubmit,
    register,
    errors,
    clearError,
    formState,
    getValues,
    setValue,
    triggerValidation,
  } = useForm({
    validationSchema: conclusionSchema,
    mode: 'onBlur',
    defaultValues: initialData || {},
  });

  useEffect(() => {
    // hold a reference to form values to save on unmount
    if (formState.dirty && getValuesRef.current !== null) {
      getValuesRef.current = getValues;
    }
  }, [getValues, formState.dirty]);

  useRegisterValidationFields(CONCLUSION_STE_FIELDS, register);

  useEffect(() => {
    setValue('receivedCompensation', receivedCompensation);
  }, [setValue, receivedCompensation]);

  useEffect(() => {
    setValue('receivedCompensationSum', receivedCompensationSum, !!receivedCompensationSum);
  }, [setValue, receivedCompensationSum]);

  useEffect(() => {
    setValue('hasAgreed', hasAgreed, !!hasAgreed);
  }, [setValue, hasAgreed]);

  const onSubmit = (data) => {
    getValuesRef.current = null; // no need to hold values anymore, we have the valid data
    dispatch(updateCascoForm({ name: 'conclusion', data }));
    dispatch(submitForm());
    goNext(step);
  };

  const triggerPhonesValidation = () => {
    if (errors.phone) {
      triggerValidation({ name: 'phone' });
    }
    if (errors.cellPhone) {
      triggerValidation({ name: 'cellPhone' });
    }
  };

  const triggerEmailConfirmationValidation = () => {
    if (getValues().emailConfirmation) {
      triggerValidation({ name: 'emailConfirmation' });
    }
  };

  return (
    <div ref={scrollTo} className={styles.formContainer}>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
      />
      <FormSection title="claim.conclusion.contacts" icon={UserInfoIcon}>
        <Grid container spacing={4}>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.conclusion.phone"
              name="phone"
              handleChange={triggerPhonesValidation}
              register={register}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.conclusion.cellPhone"
              name="cellPhone"
              handleChange={triggerPhonesValidation}
              register={register}
              required
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.conclusion.email"
              name="email"
              type="email"
              handleChange={triggerEmailConfirmationValidation}
              register={register}
              required
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.conclusion.emailConfirmation"
              name="emailConfirmation"
              type="email"
              handleChange={triggerEmailConfirmationValidation}
              register={register}
              required
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="claim.conclusion.receivedCompensationLabel" icon={UserInfoIcon}>
        <Grid container spacing={2}>
          {RECEIVED_COMPENSATION_OPTIONS.map(({ value, label }) => (
            <Grid key={label} item xs={12}>
              <MainRadio
                name="receivedCompensation"
                labelKey={label}
                value={value}
                checked={value === receivedCompensation}
                onChange={() => {
                  setReceivedCompensation(value);
                  setReceivedCompensationSum(null);
                }}
              />
            </Grid>
          ))}
        </Grid>
        {receivedCompensation === 'received' && (
          <Grid container spacing={4}>
            <Grid item md={6} xs={12}>
              <TextInputFieldWithValidation
                formProps={{ errors, clearError }}
                labelKey="claim.conclusion.receivedCompensationSum"
                name="receivedCompensationSum"
                value={
                  receivedCompensationSum ? receivedCompensationSum.toLocaleString('en-US') : ''
                }
                handleChange={(e) => {
                  const currentValue = e.target.value;
                  const fixedValue = currentValue.replace(NON_DIGITS_PATTERN, '');
                  setReceivedCompensationSum(Number(fixedValue));
                }}
                required
              />
            </Grid>
          </Grid>
        )}
      </FormSection>
      <Box sx={{ mt: '1rem' }}>
        <InfoText textInfo={t('claim.conclusion.cascoAgreementDeclaration')} withHTML />
        <Validation name="hasAgreed" errors={errors}>
          <label
            className={cs(styles.agreement, {
              [styles.invalid]: !!errors.hasAgreed,
            })}
            htmlFor="hasAgreed"
          >
            <MainCheckbox
              checked={hasAgreed}
              onChange={() => setHasAgreed((current) => !current)}
              color="primary"
              name="hasAgreed"
            />
            <span
              className={styles.agreementText}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: t('claim.conclusion.agreement'),
              }}
            />
          </label>
        </Validation>
      </Box>
    </div>
  );
};

CascoConclusionStep.propTypes = {
  step: PropTypes.shape({
    claimForm: PropTypes.string.isRequired,
    n: PropTypes.number.isRequired,
  }).isRequired,
  goNext: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    scrollTo: PropTypes.func.isRequired,
  }).isRequired,
};

export default CascoConclusionStep;
